import { lazy, memo, Suspense } from 'react'
import { isValidElementType } from 'react-is'
import { isString, classNames } from '../../framework/utils'
import { FaIcon } from '../../libs/FaIcon'
import { isLazyType } from '../mainOneappNew/navUtils'
import { blueSteelScale } from '@panwds/tailwindcss'
import type { VFC, CSSProperties, ComponentType, ElementType } from 'react'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { ComponentOrLazyFactory } from '../../framework/microAppTypes'

export const DEFAULT_COLOR = '#fff'
export const DEFAULT_SVG_COLOR = blueSteelScale[300]

const NavIcon: VFC<{ icon?: ElementType | string, className?: string, style?: CSSProperties }> = ({ icon, className, style }) => {
  if (!icon) {
    return null
  }
  const cls = classNames(className, 'sparky-nav-icon')
  if (!isString(icon)) {
    let isLazyIcon = isLazyType(icon as ComponentType)
    let iconFn = icon as unknown as ComponentOrLazyFactory
    if (!isLazyIcon && iconFn.lazy) {
      iconFn = lazy(iconFn as any)
      isLazyIcon = true
    }
    if (isLazyIcon) {
      const Icon = iconFn as ElementType
      return (
        <Suspense fallback={null}>
          <Icon className={cls} style={style} />
        </Suspense>
      )
    }
    if (isValidElementType(icon)) {
      const Icon = icon
      return <Icon className={cls} style={style} />
    }
    if ((icon as IconDefinition).iconName && (icon as IconDefinition).prefix?.startsWith('fa') && (icon as IconDefinition).icon) {
      return (
        <i className={cls} style={style}>
          <FaIcon faIconDef={icon} addClassName='fa-nav-icon' size='md' color={style?.color || DEFAULT_COLOR} />
        </i>
      )
    }
  }
  else if (icon.startsWith('data:')) {
    let dataUrl = icon
    if (icon.startsWith('data:image/svg+xml')) {
      const regex = /\bfill=['"](?:white|(?:#|%23)fff(?:fff)?)['"]/ig
      const fill = `fill='${DEFAULT_SVG_COLOR}'`
      if (icon.startsWith('data:image/svg+xml;base64,')) { // base64 encoded svg
        const svgDecoded = btoa(icon.slice(26))
        const svgUpdated = atob(svgDecoded.replace(regex, fill))
        dataUrl = `data:image/svg+xml;base64,${svgUpdated}))}`
      }
      else { // url encoded svg
        dataUrl = icon.replace(regex, encodeURIComponent(fill))
      }
    }
    return <img className={cls} src={dataUrl} alt=' ' style={style} />
  }
  else if (icon.startsWith('https:')) {
    return <img className={cls} src={icon} alt=' ' style={style} />
  }
  else {
    return <span className={classNames(cls, icon)} style={style} />
  }
  return null
}

export default memo(NavIcon)
